import { Box, Flex, Input, Select, SimpleGrid, Text, useToast } from '@chakra-ui/react';
import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import ActivityTable from 'components/ActivityTable';
import ErrorMessage from 'components/ErrorMessage';
import GoBackButton from 'components/GoBackButton';
import LoadingSpinner from 'components/LoadingSpinner';
import TOAST from 'constants/TOAST';
import createCancelTokenSource from 'utils/createCancelTokenSource';
import { fetchManyCategories } from 'services/fetch/activityCategories';
import { fetchManyOldActivitiesByEvent, fetchOneActivity } from 'services/fetch/activities';
import { fetchManyRegistriesByActivity } from 'services/fetch/registries';
import { fetchOneEvent } from 'services/fetch/events';
import renderEventName from 'utils/renderEventName';
import { ActivityCategory, ActivityType, EventType } from 'types/models';
import {
    downloadMinistrantsXls,
    downloadPresenceListPdf,
    downloadRegistryPdf,
    downloadRegistryXls,
} from 'utils/reportUtils';
import {
    DashboardPageContent,
    FiltersWrapper,
    FormWrapper,
    PageSubtitle,
    PageTitle,
} from 'custom-style-components';

interface ParamTypes {
    eventId: string;
}

const ListActivities: React.FC = () => {
    const toast = useToast({
        duration: TOAST.DURATION,
        position: TOAST.DEFAULT_POSITION,
        isClosable: TOAST.CLOSABLE_BY_DEFAULT,
    });

    const { eventId } = useParams<ParamTypes>();
    const { pathname } = useLocation();

    const [event, setEvent] = useState<EventType>();
    const [categoryList, setCategoryList] = useState<ActivityCategory[]>();
    const [activityList, setActivityList] = useState<ActivityType[]>([]);
    const [activityShow, setActivityShow] = useState<ActivityType[]>([]);
    const [activityName, setActivityName] = useState('');
    const [debouncedActivityName, setDebouncedActivityName] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<number>();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const source = useRef(createCancelTokenSource());
    const timeoutRef = useRef<number>();

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedActivityName(activityName);
        }, 400);
        return () => clearTimeout(handler);
    }, [activityName]);

    const downloadXls = useCallback((activityId: number, extension: 'xls' | 'xlsx') => {
        toast({ title: `Gerando presenças .${extension}, aguarde...`, status: 'info', duration: 4000 });
        const src = createCancelTokenSource();
        fetchOneActivity(src.token, activityId.toString())
            .then((activity) => {
                return fetchManyRegistriesByActivity(src.token, activityId.toString(), {
                    limit: Number.MAX_SAFE_INTEGER,
                }).then(({ registries }) => ({ activity, registries }));
            })
            .then(({ activity, registries }) => {
                setTimeout(() => {
                    downloadRegistryXls(activity, registries, extension);
                }, 150);
            });
    }, [toast]);

    const downloadPdf = useCallback((activityId: number) => {
        toast({ title: 'Gerando relatório .pdf, aguarde...', status: 'info', duration: 4000 });
        const src = createCancelTokenSource();
        fetchOneActivity(src.token, activityId.toString())
            .then((activity) => {
                return fetchManyRegistriesByActivity(src.token, activityId.toString(), {
                    limit: Number.MAX_SAFE_INTEGER,
                }).then(({ registries }) => ({ activity, registries }));
            })
            .then(({ activity, registries }) => {
                setTimeout(() => {
                    downloadRegistryPdf(activity, registries);
                }, 150);
            });
    }, [toast]);

    const downloadXlsMinisters = useCallback((activityId: number, extension: 'xls' | 'xlsx') => {
        toast({ title: `Gerando ministrantes .${extension}, aguarde...`, status: 'info', duration: 4000 });
        const src = createCancelTokenSource();
        fetchOneActivity(src.token, activityId.toString()).then((result) => {
            setTimeout(() => {
                downloadMinistrantsXls(result, extension);
            }, 150);
        });
    }, [toast]);

    const downloadPdfPresence = useCallback((activityId: number) => {
        toast({ title: 'Gerando lista de presença .pdf, aguarde...', status: 'info', duration: 4000 });
        const src = createCancelTokenSource();
        fetchOneActivity(src.token, activityId.toString())
            .then((activity) => {
                return fetchManyRegistriesByActivity(src.token, activityId.toString(), {
                    limit: Number.MAX_SAFE_INTEGER,
                }).then(({ registries }) => ({ activity, registries }));
            })
            .then(({ activity, registries }) => {
                setTimeout(() => {
                    downloadPresenceListPdf(activity, registries);
                }, 150);
            });
    }, [toast]);

    const onChangeCategory = (ev: ChangeEvent<HTMLSelectElement>) => {
        if (ev.target.value != undefined) {
            setLoading(true);

            if (timeoutRef.current) clearTimeout(timeoutRef.current);

            timeoutRef.current = window.setTimeout(() => {
                setSelectedCategory(
                    ev.target.value === '' ? undefined : parseInt(ev.target.value),
                );
            }, 200);
        }
    };

    const loadActivities = useCallback(() => {
        source.current = createCancelTokenSource();
        setLoading(true);
        fetchOneEvent(source.current.token, eventId)
            .then((ev) => {
                setEvent(ev);
                return fetchManyOldActivitiesByEvent(source.current.token, eventId, {
                    limit: Number.MAX_SAFE_INTEGER,
                    category: selectedCategory,
                });
            })
            .then(({ oldActivities }) => {
                setActivityList(oldActivities);
            })
            .catch((err) => {
                setError(err.message);
                toast({ title: err.message, status: 'error' });
            })
            .finally(() => setLoading(false));
    }, [eventId, selectedCategory, toast]);

    useEffect(() => {
        fetchManyCategories(source.current.token, { limit: Number.MAX_SAFE_INTEGER })
            .then((result) => setCategoryList(result.categories))
            .catch((err) => {
                setError(err.message);
                toast({ title: err.message, status: 'error' });
            });
    }, [toast]);

    useEffect(() => {
        loadActivities();
        return () => source.current.cancel();
    }, [loadActivities]);

    useEffect(() => {
        const lower = debouncedActivityName.toLowerCase();
        setActivityShow(
            activityList.filter(
                (a) =>
                    a.title.toLowerCase().includes(lower) ||
                    a.activityCategory.description.toLowerCase().includes(lower),
            ),
        );
    }, [debouncedActivityName, activityList]);

    return (
        <DashboardPageContent>
            <FormWrapper>
                <PageTitle>Atividades anteriores</PageTitle>
                <PageSubtitle>{event && renderEventName(event)}</PageSubtitle>
                <GoBackButton />

                {/* Filtros e Pesquisa */}
                <FiltersWrapper>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="100%" mt={4} mb={6}>
                        <Box>
                            <Text fontSize="xl" fontWeight="bold" mb={2} color="gray.700">
                                Categorias
                            </Text>
                            {categoryList && categoryList.length ? (
                                <Select
                                    bg="white"
                                    size="lg"
                                    fontSize="xl"
                                    h="50px"
                                    placeholder="Todas as categorias"
                                    onChange={onChangeCategory}
                                    focusBorderColor="blue.400"
                                    shadow="sm"
                                    borderRadius="md"
                                >
                                    {categoryList.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.description}
                                        </option>
                                    ))}
                                </Select>
                            ) : (
                                <Text fontSize="lg" color="gray.500">
                                    Não foi possível carregar a lista de categorias
                                </Text>
                            )}
                        </Box>

                        <Box>
                            <Text fontSize="xl" fontWeight="bold" mb={2} color="gray.700">
                                Pesquisar atividades
                            </Text>
                            <Input
                                size="lg"
                                fontSize="xl"
                                h="50px"
                                bg="white"
                                type="text"
                                value={activityName}
                                onChange={(e) => setActivityName(e.target.value)}
                                placeholder="Pesquise por nome ou categoria"
                                focusBorderColor="blue.400"
                                shadow="sm"
                                borderRadius="md"
                                isDisabled={activityList.length === 0}
                            />
                        </Box>
                    </SimpleGrid>
                </FiltersWrapper>

                {error ? (
                    <ErrorMessage>{error}</ErrorMessage>
                ) : loading ? (
                    <Flex justify="center" p={10}>
                        <LoadingSpinner />
                    </Flex>
                ) : (
                    <ActivityTable
                        activities={activityShow}
                        pathname={pathname}
                        showInscritosAction
                        onDownloadPresencasXls={downloadXls}
                        onDownloadMinistrantesXls={downloadXlsMinisters}
                        onDownloadListaPresencaPdf={downloadPdfPresence}
                        onDownloadRelatorioPdf={downloadPdf}
                    />
                )}
            </FormWrapper>
        </DashboardPageContent>
    );
};

export default ListActivities;